import React, { createContext, useContext, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api, TOKEN_KEY, USER_KEY, setBerechtigungsWechselHandler } from './api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem(USER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const [loading, setLoading] = useState(!!localStorage.getItem(TOKEN_KEY));
  const [permMatrix, setPermMatrix] = useState(null);

  const loadPermissions = React.useCallback(async () => {
    try {
      const r = await api.get('/permissions');
      setPermMatrix(r.data.matrix || {});
    } catch { setPermMatrix({}); }
  }, []);

  // Lehnt der Server eine Anfrage mangels Berechtigung ab, kann das zwei
  // Ursachen haben:
  //
  //   1. Ein Administrator hat die Rechte geändert, während die Sitzung offen
  //      war – dann arbeitet dieser Browser mit einem veralteten Stand und ein
  //      Neuladen ist richtig.
  //   2. Die Oberfläche fragt etwas ab, das diese Rolle nie durfte. Dann
  //      wäre Neuladen falsch: Nach dem Laden passiert dasselbe wieder, und
  //      die Seite lädt endlos neu. Genau das war die Dauermeldung auf dem
  //      Handy.
  //
  // Unterschieden wird, indem die Rechte frisch geholt und mit dem
  // zwischengespeicherten Stand verglichen werden. Nur bei echtem Unterschied
  // wird neu geladen – und das höchstens einmal pro Sitzung.
  useEffect(() => {
    let laeuft = false;
    setBerechtigungsWechselHandler(async () => {
      if (laeuft) return;
      laeuft = true;
      try {
        const vorher = JSON.stringify(permMatrix ?? {});
        const r = await api.get('/permissions');
        const nachher = JSON.stringify(r.data.matrix || {});
        setPermMatrix(r.data.matrix || {});

        const schonNeugeladen = sessionStorage.getItem('fd_perm_reload') === '1';
        if (vorher !== nachher && !schonNeugeladen) {
          sessionStorage.setItem('fd_perm_reload', '1');
          toast.info('Ihre Berechtigungen wurden geändert. Die Ansicht wird aktualisiert.');
          setTimeout(() => { window.location.href = '/'; }, 1200);
          return;
        }
        // Unverändert: Die Oberfläche hat etwas abgefragt, das dieser Rolle
        // ohnehin nicht offensteht. Kein Neuladen, keine Meldung – die
        // betroffene Stelle zeigt dann einfach keine Daten an.
      } catch {
        // Rechte nicht abrufbar – dann sicher nichts tun.
      } finally {
        // Nach kurzer Pause wieder scharf, damit eine spätere echte Änderung
        // noch erkannt wird.
        setTimeout(() => { laeuft = false; }, 5000);
      }
    });
    return () => setBerechtigungsWechselHandler(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permMatrix]);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setLoading(false); return; }
    api.get('/auth/me').then(r => {
      setUser(r.data);
      localStorage.setItem(USER_KEY, JSON.stringify(r.data));
      loadPermissions();
    }).catch(() => {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      setUser(null);
    }).finally(() => setLoading(false));
  }, [loadPermissions]);

  const login = async (email, password, remember_me = false) => {
    const { data } = await api.post('/auth/login', { email, password, remember_me });
    // Neue Sitzung: Die Einmal-Sperre gegen Neulade-Schleifen zurücksetzen.
    sessionStorage.removeItem('fd_perm_reload');
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    setUser(data.user);
    await loadPermissions();
    return data.user;
  };

  const onboarding = async (payload) => {
    const { data } = await api.post('/auth/onboarding', payload);
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    setUser(data.user);
    await loadPermissions();
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
    window.location.href = '/login';
  };

  const refreshUser = async () => {
    try {
      const r = await api.get('/auth/me');
      setUser(r.data);
      localStorage.setItem(USER_KEY, JSON.stringify(r.data));
    } catch {}
  };

  const isAdmin = user?.role === 'admin';

  // Berechtigungsprüfung: Admin darf alles. Sonst gemäß Matrix.
  const can = React.useCallback((resource, action = 'view') => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    if (!permMatrix) return false;
    return !!(permMatrix[user.role]?.[resource]?.[action]);
  }, [user, permMatrix]);

  const value = { user, loading, login, logout, onboarding, refreshUser, isAdmin, can, permMatrix, loadPermissions };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
